import { Recurrence } from '@tableau/self-service-schedules';
import { SelfServiceSchedulePane, SelfServiceSchedulePaneType } from '@tableau/self-service-schedules/lib/src/components/SelfServiceSchedulePane';
import { SiteUrls } from '@tableau/shared-urls';
import { ButtonType, DialogFooterButtonsWidget, DropdownButtonType, List, ListProps, RadioButtonWithLabelGroupWidget, RadioButtonWithLabelOption, RadioLayoutType, SingleSelectDropdown, TabStyles } from '@tableau/widgets';
import * as React from 'react';
import { useState } from 'react';

import { Messages } from '../../codegen/Localize';
import { IRemoteRefreshAgentOption } from '../../datasource/services/ChangeDatasourceRemoteRefreshPropertiesHelper';
import { getSessionManagementServiceInstance } from '../../sessionManagement/SessionManagementService';
import { cssClass, Style } from '../../style';
import { RemoteRefreshTypes } from '../../ts/ServerApi/requests/GetRemoteRefreshSchedulesRequest';
import { localizeRefreshTargetType } from './EditRemoteRefreshScheduleAction';

export interface BridgeExtractScheduleDialogProps {
    recurrence: Recurrence;
    scheduleOnClientPrompt: string;
    remoteAgentOptions: IRemoteRefreshAgentOption[];
    disableIncremental: boolean;
    showExistingTasksWillBeDeleteWarning: boolean;
    close: () => void;
    scheduleExtract: (recurrence: Recurrence, remoteAgentName: string, refreshType: string) => void;
}

export interface BridgeExtractScheduleDialogState {
    recurrence: Recurrence;
    selectedAgentName: string;
    refreshTypeSelectedIndex: number;
}

export function getAgentListItems(remoteAgentOptions: IRemoteRefreshAgentOption[], selectedName: string): React.ReactElement<ListProps> {
    return <List items={remoteAgentOptions.map((agent: IRemoteRefreshAgentOption) => {
        return {
            content: <span>{agent.display}</span>,
            value: agent.name,
            testId: `bridge-extract-dropdown-${agent.name}`
        };
    })} selectedValue={selectedName}
        selectOnEnterKey={true}/>;
}

export function BridgeExtractScheduleDialogBody(props: BridgeExtractScheduleDialogProps) {
    const initialDropdownText = props.remoteAgentOptions.length === 0 ? Messages.remoteRefreshModeEditRemoteSelectNone() : Messages.remoteRefreshModeEditRemoteSelectEmpty();
    const refreshTypeOptions = Object.values(RemoteRefreshTypes);
    const [recurrence, setRecurrence] = useState(props.recurrence);
    const [selectedAgentName, setSelectedAgentName] = useState(initialDropdownText);
    const [refreshTypeSelectedIndex, setRefreshTypeSelectedIndex] = useState(0);

    const refreshTypeItems: RadioButtonWithLabelOption<string>[] =
        refreshTypeOptions.map((type: string) => ({
            value: type,
            testId: `radio-buttom-${type}`,
            label: localizeRefreshTargetType(type)
    }));

    const server = getSessionManagementServiceInstance();
    const userAccountSettingsLink = new SiteUrls({
        siteUrlName: server.sessionSiteUrlName(),
        queryString: window.location.search
    }).userSettingsUrl(server.sessionUserInfo().username, server.sessionUserInfo().domainName);
    const timeZoneDetails = {
        timeZonesEnabled: true,
        timeZoneDisplayName: server.userTimeZone().displayName,
        linkToUserSettings: userAccountSettingsLink
    };

    const headerStyle: Style = {
        fontWeight: 'normal',
        fontSize: 20,
        paddingBottom: 4
    };
    const headerCSS = cssClass('bridge-extract', 'header', headerStyle);
    return <div>
        <h1 className={headerCSS}>{Messages.extractTasksDialogTitle_ScheduleLegacyBridgeRefresh()}</h1>
        <span>{props.scheduleOnClientPrompt}</span>
        <div style={{ marginTop: 8, marginBottom: 18 }}>
            <span>{Messages.remoteRefreshAgentSelectV2()}</span>
            <SingleSelectDropdown
                testId={'bridge-extract-dropdown'}
                type={DropdownButtonType.Outline}
                list={getAgentListItems(props.remoteAgentOptions, selectedAgentName)}
                disabled={props.remoteAgentOptions.length === 0}
                value={selectedAgentName}
                onChange={(agentName: string) => setSelectedAgentName(agentName)}
                style={{ width: 200, marginTop: 5 }}
                label={''}
            />
        </div>
        <div style={{ marginBottom: 18 }}>
            <span>{Messages.remoteRefreshSelectSchedule()}</span>
            <div style={{ marginTop: 5 }}>
                <SelfServiceSchedulePane
                    recurrenceModel={recurrence}
                    recurrenceModelChangedCallback={(recurrence) => setRecurrence(recurrence)}
                    timeZoneDetails={timeZoneDetails}
                    type={SelfServiceSchedulePaneType.BridgeExtractRefresh}
                    uncollapsible={false}
                    onTimeZoneClick={props.close}
                />
            </div>
        </div>
        <div style={{ marginBottom: 8 }}>
            <span>{Messages.remoteRefreshAgentSelectV2Note()}</span>
        </div>
        <div style={{ marginTop: 30, marginBottom: 8 }}>
            {!props.disableIncremental && <>
            <RadioButtonWithLabelGroupWidget
                label={Messages.createRemoteRefreshScheduleDialogTypePrompt()}
                ariaLabel='The type of extract to schedule'
                buttonList={refreshTypeItems}
                checkedIndex={refreshTypeSelectedIndex}
                handleChange={(_: string, checkedIndex: number) => {
                    setRefreshTypeSelectedIndex(checkedIndex);
                }}
                layout={RadioLayoutType.Veritical}
            />
            </>}
        </div>
        <div style={{ marginBottom: 8, color: TabStyles.Colors.AttentionDark}}>
            {props.showExistingTasksWillBeDeleteWarning && <>
            <span>{Messages.onlineSchedulesDeletedWarning()}</span>
            </>}
        </div>
        <DialogFooterButtonsWidget
            buttons={[
                {
                    handleClick: () => props.close(),
                    buttonType: ButtonType.Outline,
                    children: Messages.cancel(),
                    testId: 'bridge-footer-cancel'
                },
                {
                    handleClick: () => props.scheduleExtract(
                        recurrence,
                        selectedAgentName,
                        refreshTypeOptions[refreshTypeSelectedIndex]
                    ),
                    buttonType: ButtonType.Primary,
                    children: Messages.extractTaskCreateLegacyBridgeScheduleConfirm(),
                    disabled: selectedAgentName === initialDropdownText,
                    testId: 'bridge-footer-confirm',
                }
            ]}
            testId="bridge-extract-dialog-footer"
        />
    </div>;
}
